import React from 'react';
import { TypeAnimation } from 'react-type-animation';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { HiArrowNarrowRight } from 'react-icons/hi';
import { FaLaptopCode, FaTerminal, FaRocket, FaFire } from 'react-icons/fa';

const Hero = () => {
  return (
    <div id="home" className="w-full h-screen bg-transparent relative overflow-hidden flex items-center justify-center pt-24">
      {/* Background blobs */}
      <div className="blob w-[300px] h-[300px] bg-accent-primary top-[10%] left-[15%]" />
      <div className="blob w-[400px] h-[400px] bg-accent-purple bottom-[15%] right-[20%]" />
      <div className="blob w-[250px] h-[250px] bg-accent-indigo top-[40%] right-[30%]" />

      <div className="section-padding flex flex-col md:flex-row items-center justify-between w-full">
        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col gap-4 text-left z-10 w-full md:w-2/3"
        >
          <p className="text-accent-primary font-mono text-lg font-medium tracking-widest pl-1">
            Hi, I'm
          </p>
          <h1 className="text-5xl sm:text-7xl font-display font-bold text-white tracking-tight leading-[1.1] sm:leading-[1.15]">
            Yash <span className="text-accent-primary group-hover:animate-pulse">Patel</span>
          </h1>
          <div className="flex items-center gap-3 text-2xl sm:text-4xl text-slate-400 font-display font-medium min-h-[60px]">
            <span>I'm a</span>
            <TypeAnimation
              sequence={[
                'MERN Stack Developer',
                2000,
                'React Expert',
                2000,
                'Problem Solver',
                2000,
              ]}
              wrapper="span"
              speed={50}
              repeat={Infinity}
              className="text-accent-primary typewriter-cursor"
            />
          </div>
          <p className="text-slate-400 py-4 max-w-[540px] text-lg leading-relaxed">
            I build scalable web apps that solve real problems. Specializing in modern technologies like React, Node.js, and Spring Boot to create seamless digital solutions.
          </p>
          <div className="flex flex-wrap gap-4 mt-4">
            <Link to="projects" smooth={true} duration={500} offset={-70}>
              <button className="btn-secondary flex items-center group">
                View My Work
                <HiArrowNarrowRight className="ml-3 group-hover:rotate-90 duration-300 transform" />
              </button>
            </Link>
            <button className="btn-primary">Download Resume</button>
          </div>
        </motion.div>

        {/* Right Decorative Code Card */}
        <motion.div
          initial={{ opacity: 0, x: 50, rotate: 5 }}
          animate={{ opacity: 1, x: 0, rotate: -2 }}
          transition={{ duration: 1, type: 'spring' }}
          className="hidden md:flex flex-col z-10 w-1/3 relative"
        >
          <div className="glass p-6 rounded-2xl glow shadow-2xl animate-float group hover:scale-[1.02] transition-transform">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-[#ff5f56]" />
              <div className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
              <div className="w-3 h-3 rounded-full bg-[#27c93f]" />
              <div className="ml-auto text-xs font-mono text-slate-500 uppercase tracking-widest">Code Snippet</div>
            </div>
            <pre className="text-xs sm:text-sm font-mono text-accent-secondary overflow-hidden">
              <code>{`const developer = {
  name: 'Yash Patel',
  role: 'MERN Stack Developer',
  skills: ['MongoDB', 'Express', 'React', 'Node.js'],
  passions: ['AI', 'Scalability'],
  hireable: true,
  solve: (problem) => {
    return code(problem);
  }
};`}</code>
            </pre>
            <div className="mt-6 flex justify-around opacity-40 group-hover:opacity-100 transition-opacity">
              <FaLaptopCode className="text-2xl" />
              <FaTerminal className="text-2xl text-accent-indigo" />
              <FaRocket className="text-2xl text-accent-purple" />
              <FaFire className="text-2xl text-red-500" />
            </div>
          </div>

          {/* Subtle glow behind card */}
          <div className="absolute inset-0 -z-10 bg-accent-primary/20 blur-[60px] rounded-full scale-125 translate-x-1/2" />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 hidden sm:block"
      >
        <div className="w-6 h-10 border-2 border-slate-500 rounded-full flex justify-center p-1">
          <div className="w-1 h-3 bg-accent-primary rounded-full" />
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;
